import { useMemo } from 'react';
import styled from 'styled-components';

import { Flex, Space, Sticky, VFlex } from '@/components/base';
import { SentimentTrendChart } from '@/components/Chart/SentimentTrendChart/SentimentTrendChart';
import { GuideInfo } from '@/components/Chart/SentimentTrendChart/SentimentTrendChartTitle';
import ChatBox from '@/components/ChatBlock/ChatBox';
import InfoBox from '@/components/ChatBlock/InfoBox';
import ToggleRadio from '@/components/Common/ToggleRadio';
import Typo from '@/components/Typography/Typo';
import SentimentInfo from '@/features/Feedback/field/Sentiment/SentimentInfo';
import useColor from '@/hooks/domain/useColor';
import {
  FEEDBACK_DETAIL_MODAL_SCROLL_SELECTOR,
  SOURCE_TYPE,
} from '@/types/feedbackConst';
import { getTimezoneDayjs } from '@/utils/time';

import useContentBodyConfig from '../useContentBodyConfig';

type Props = {
  feedbackDetailFullView: boolean;
  feedbackAlias: string;
  detail?: FeedbackT;
  focusedBlock?: string;
};

const ChatMessageDisplay = (props: Props) => {
  const color = useColor();

  const {
    chat_title,
    chat_messages,
    chatRef,
    showCustomerOnly,
    selectedChat,
    sentimentChartData,
    setShowCustomerOnly,
    onChartCirlceClick,
  } = useContentBodyConfig({
    isFullView: props.feedbackDetailFullView,
    chat_messages: props.detail?.chat_text,
    scrollSelector: `#${FEEDBACK_DETAIL_MODAL_SCROLL_SELECTOR(
      props.feedbackAlias,
    )}`,
    blocks: props.detail?.blocks || [],
    sentences: props.detail?.sentences || [],
  });

  const sourceType = props.detail?.source.type;

  const authorColorMap = useMemo(() => {
    if (sourceType !== SOURCE_TYPE.DISCORD) return undefined;
    const uniqueAuthor = {} as { [key: string]: boolean };
    const authorMap = {} as { [key: string]: string };
    for (const message of chat_messages) {
      if (message.author && message.author.name) {
        uniqueAuthor[message.author.name] = true;
      }
    }
    let k = 0;
    for (const key of Object.keys(uniqueAuthor)) {
      authorMap[key] = color.profile[k % 12];
      k += 1;
    }
    return authorMap;
  }, [sourceType, chat_messages, color.profile]);

  const sentimentTrendChartLabels = useMemo(() => {
    return sentimentChartData.map((data, index) => {
      if (index === 0 || index === sentimentChartData.length - 1) {
        return getTimezoneDayjs(data.date).format('HH:mm');
      }
    });
  }, [sentimentChartData]);

  const filteredChat = useMemo(() => {
    return chat_messages.filter((message) => {
      if (showCustomerOnly && message.text) {
        return (
          message.author?.type === 3 ||
          message.type === 'log' ||
          message.type === 'time'
        );
      }
      return !!message.text;
    });
  }, [chat_messages, showCustomerOnly]);

  return (
    <>
      <Flex className="chat-display-header" a="center" j="space-between">
        <Typo type="label2">{chat_title}</Typo>
        <Flex g="6px" a="center">
          <Typo type="label3" color={color.gray600}>
            show contact feeds only
          </Typo>
          <ToggleRadio
            status={showCustomerOnly}
            onChange={(status) => setShowCustomerOnly(status)}
          />
        </Flex>
      </Flex>
      <Sticky
        style={{
          background: color.gray100,
          margin: '8px 0 16px',
          zIndex: 1,
          boxShadow: `0px 6px 8px ${color.gray100}`,
        }}
      >
        <VFlex g={8}>
          <Flex a="center" g="4px">
            <Flex a="center" g="1px">
              <Typo type="label3" color={color.gray700} weight="sb">
                Sentiment trend
              </Typo>
              <GuideInfo
                tooltip={{
                  content: (
                    <SentimentInfo>
                      <SentimentInfo.SentimentColor />
                      <SentimentInfo.KeySentiment />
                    </SentimentInfo>
                  ),
                  offset: {
                    top: -20,
                    left: 10,
                  },
                }}
              />
            </Flex>
          </Flex>

          {props.detail?.blocks && (
            <SentimentTrendChart
              onCircleClick={onChartCirlceClick}
              data={sentimentChartData}
              mode="even"
              labels={sentimentTrendChartLabels}
            />
          )}
        </VFlex>
      </Sticky>

      <Style>
        <div ref={chatRef}>
          <VFlex g={4}>
            {filteredChat.map((message, index) => {
              if (showCustomerOnly && message.type !== 'time') {
                if (!(message.type === 'chat' && message.author?.type === 3))
                  return null;
              }
              if (message.type === 'log' || message.type === 'time') {
                return (
                  <InfoBox
                    key={
                      message.source_id
                        ? `${message.source_id}-${message.type}`
                        : index
                    }
                    text={message.text}
                    parseMDtoHTML
                  />
                );
              }

              return (
                <ChatBox
                  key={
                    message.source_id
                      ? `${message.source_id}-${message.type}-${index}`
                      : index
                  }
                  source_id={message.source_id}
                  customer_info={props.detail?.customer_info}
                  message={message}
                  originalHTML={sourceType === SOURCE_TYPE.ATLAS}
                  multiChat={sourceType === SOURCE_TYPE.DISCORD}
                  authorColorMap={authorColorMap}
                  focused={selectedChat === index.toString()}
                  blocks={props.detail?.blocks || []}
                  focusedBlock={props.focusedBlock}
                />
              );
            })}
          </VFlex>
          <Space h="48px" />
        </div>
      </Style>
    </>
  );
};

const Style = styled.div`
  overflow: hidden;
  position: relative;
  transition: all 0.3s;
  .chat-display-header {
    color: ${(props) => props.theme.colors.gray600};
    background-color: ${(props) => props.theme.colors.gray200};
    height: 40px;
    padding: 0 16px;
  }
  .btn-show-more {
    background-color: ${(props) => props.theme.colors.gray300};
    color: ${(props) => props.theme.colors.gray600};
    padding: 4px 12px;
    border-radius: 16px;
    cursor: pointer;
    position: absolute;
    bottom: 16px;
    z-index: 10;
  }
  .show-more-gradient {
    position: absolute;
    bottom: 0;
    width: 100%;
    height: 64px;
    left: 0;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0) 9.33%,
      ${(props) => props.theme.colors.gray100} 82.17%
    );
    z-index: 9;
  }
`;

export default ChatMessageDisplay;
